/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	type CSSProperties,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { jsx } from '@compiled/react';

import { css, type StrictXCSSProp, type XCSSAllProperties } from '@atlaskit/css';

import {
	buildOrderIndex,
	isSameOrderIndex,
	measureRects,
	prefersReducedMotion,
	type RectMap,
} from './utils';

export type FlipListRenderItem = (key: string) => React.ReactNode;

type Point = { x: number; y: number };

type TransformMap = Map<string, Point>;

type Phase = 'idle' | 'inverting' | 'playing';

export type FlipListProps = {
	/**
	 * Ordered list of item keys that represents the desired visual ordering.
	 * Keys must be stable.
	 */
	order: readonly string[];
	/** Render an item for the given key. */
	renderItem: FlipListRenderItem;
	/** Optional wrapper props for each item (event handlers, data attributes, etc.). */
	getItemProps?: (key: string) => React.HTMLAttributes<HTMLElement>;
	/** Disable animation (e.g. while user is interacting/hovering). */
	disabled?: boolean;
	/** Animation duration in ms. */
	durationMs?: number;
	/** CSS easing function. */
	easing?: string;
	/** Container element tag. Use this to preserve semantics (e.g. `ul`). */
	containerAs?: React.ElementType;
	/** Optional props for the container element. */
	containerProps?: Omit<React.HTMLAttributes<HTMLElement>, 'css' | 'className' | 'style'>;
	/** Item wrapper element tag. Must be an element that supports transforms. */
	itemAs?: React.ElementType;
	/**
	 * Use CSS `order` (via `--flip-order`) instead of reordering DOM nodes.
	 * Default behavior reorders the DOM to match `order`.
	 */
	useCssOrder?: boolean;
	/**
	 * Optional: if true, keep DOM nodes for keys that disappear (default: true).
	 * When false, removed keys are removed from DOM.
	 */
	keepRemovedInDom?: boolean;
	/** Optional container style overrides. */
	xcss?: StrictXCSSProp<XCSSAllProperties, never>;
};

const flipListContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
});

const flipListItemStyles = css({
	// Visual ordering can be driven by CSS order.
	order: 'var(--flip-order, 0)',
	transform: 'translate(var(--translate-x, 0px), var(--translate-y, 0px))',
	transition: 'transform var(--animation-duration, 300ms) var(--animation-easing, ease)',
	willChange: 'transform',
});

const flipListItemNoTransitionStyles = css({
	transition: 'none',
});

const flipListHiddenStyles = css({
	display: 'none',
});

/**
 * FlipList
 *
 * Implements a FLIP animation. By default it reorders DOM nodes;
 * optionally it can keep DOM node order stable using CSS order.
 *
 * Expected behavior:
 * 1) All items mount with translate=(0,0)
 * 2) On reorder: measure FIRST, apply new order, measure LAST
 * 3) Apply inverse transform WITHOUT transition
 * 4) Next frame: reset transform to 0 WITH transition
 * 5) After transition completes: remain at translate=0 with new order
 */
export const FlipList: ({
	order,
	renderItem,
	getItemProps,
	disabled,
	durationMs,
	easing,
	containerAs,
	containerProps,
	itemAs,
	useCssOrder,
	keepRemovedInDom,
	xcss,
}: FlipListProps) => JSX.Element = ({
	order,
	renderItem,
	getItemProps,
	disabled = false,
	durationMs = 250,
	easing = 'cubic-bezier(0.2, 0, 0.2, 1)',
	containerAs: Container = 'div',
	containerProps,
	itemAs: Item = 'div',
	useCssOrder = false,
	keepRemovedInDom = true,
	xcss,
}: FlipListProps) => {
	const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
	const refCallbacksRef = useRef<Map<string, (el: HTMLElement | null) => void>>(new Map());

	// Stable DOM keys (CSS order) or DOM order that matches `order`.
	const [domKeys, setDomKeys] = useState<string[]>(() => [...order]);

	// Current visual order index map.
	const [orderIndex, setOrderIndex] = useState<Map<string, number>>(() => buildOrderIndex(order));

	// Current per-item transforms.
	const [transforms, setTransforms] = useState<TransformMap>(() => new Map());

	// Phase controls whether we disable transitions while applying inverse transforms.
	const [phase, setPhase] = useState<Phase>('idle');

	const firstRectsRef = useRef<RectMap | null>(null);
	const timeoutRef = useRef<number | null>(null);

	// Keep domKeys stable (CSS order) or align DOM order to `order`.
	useEffect(() => {
		setDomKeys((prev) => {
			if (useCssOrder) {
				let next = prev;
				for (const key of order) {
					if (!next.includes(key)) {
						next = next === prev ? [...prev, key] : [...next, key];
					}
				}
				if (!keepRemovedInDom) {
					next = next.filter((k) => order.includes(k));
				}
				return next;
			}

			let next = [...order];
			if (keepRemovedInDom) {
				for (const key of prev) {
					if (!next.includes(key)) {
						next.push(key);
					}
				}
			}
			return next;
		});
	}, [keepRemovedInDom, order, useCssOrder]);

	// Cleanup timers.
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				window.clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	// Capture FIRST rects and update orderIndex when desired order changes.
	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		const nextIndex = buildOrderIndex(order);

		if (disabled || prefersReducedMotion()) {
			// Jump immediately: no transforms, just update ordering.
			setPhase((prev) => (prev === 'idle' ? prev : 'idle'));
			setTransforms((prev) => (prev.size === 0 ? prev : new Map()));
			setOrderIndex((prev) => (isSameOrderIndex(prev, nextIndex, order) ? prev : nextIndex));
			firstRectsRef.current = null;
			return;
		}

		// Detect change.
		let changed = false;
		for (const key of order) {
			if (orderIndex.get(key) !== nextIndex.get(key)) {
				changed = true;
				break;
			}
		}
		if (!changed) {
			return;
		}

		// Measure FIRST (current visual layout).
		firstRectsRef.current = measureRects(domKeys, itemRefs.current);

		// Apply new visual order (CSS order values or DOM order).
		setOrderIndex(nextIndex);
		// Next layout effect will measure LAST and compute transforms.
	}, [disabled, domKeys, order, orderIndex, useCssOrder]);

	// After orderIndex changes commit, compute LAST rects and perform FLIP.
	useLayoutEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		const first = firstRectsRef.current;
		if (!first) {
			return;
		}

		if (disabled || prefersReducedMotion()) {
			firstRectsRef.current = null;
			return;
		}

		const last = measureRects(domKeys, itemRefs.current);

		const inverse: TransformMap = new Map();
		for (const key of domKeys) {
			const a = first.get(key);
			const b = last.get(key);
			if (!a || !b) {
				continue;
			}
			const dx = a.left - b.left;
			const dy = a.top - b.top;
			if (dx !== 0 || dy !== 0) {
				inverse.set(key, { x: dx, y: dy });
			}
		}

		// Invert (no transition): apply inverse transforms.
		setPhase('inverting');
		setTransforms(inverse);

		// Play (with transition): next frame, reset transforms to 0.
		window.requestAnimationFrame(() => {
			setPhase('playing');
			setTransforms(new Map());

			if (timeoutRef.current) {
				window.clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = window.setTimeout(() => {
				setPhase('idle');
			}, durationMs);
		});

		firstRectsRef.current = null;
	}, [disabled, domKeys, durationMs, orderIndex]);

	const getItemRefCallback = useCallback((key: string) => {
		const existing = refCallbacksRef.current.get(key);
		if (existing) {
			return existing;
		}
		const cb = (el: HTMLElement | null) => {
			if (el) {
				itemRefs.current.set(key, el);
			} else {
				itemRefs.current.delete(key);
			}
		};
		refCallbacksRef.current.set(key, cb);
		return cb;
	}, []);

	return (
		<Container
			{...containerProps}
			style={
				{
					'--animation-duration': `${durationMs}ms`,
					'--animation-easing': easing,
				} as CSSProperties
			}
			css={flipListContainerStyles}
			className={xcss}
		>
			{domKeys.map((key) => {
				const itemProps = getItemProps?.(key) ?? {};
				const idx = orderIndex.get(key);
				const hidden = idx === undefined;
				const t = transforms.get(key) ?? { x: 0, y: 0 };
				const cssOrderIndex = idx === undefined ? undefined : idx;
				let translateX = `${t.x}px`;
				let translateY = `${t.y}px`;
				let flipOrderValue: string | undefined;

				if (useCssOrder && cssOrderIndex !== undefined) {
					flipOrderValue = `${cssOrderIndex}`;
				}

				return (
					<Item
						key={key}
						ref={getItemRefCallback(key)}
						{...itemProps}
						style={
							{
								'--translate-x': translateX,
								'--translate-y': translateY,
								'--flip-order': flipOrderValue,
							} as CSSProperties
						}
						css={[
							flipListItemStyles,
							hidden && flipListHiddenStyles,
							(phase === 'inverting' || phase === 'idle') &&
								transforms.size > 0 &&
								flipListItemNoTransitionStyles,
						]}
					>
						{renderItem(key)}
					</Item>
				);
			})}
		</Container>
	);
};
